import { Button } from "@frontend/shared/components/ui/button";
import { cn } from "@frontend/shared/lib/utils";
import { differenceInDays, isSameDay, startOfDay } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect } from "react";
import { useMediaQuery } from "usehooks-ts";
import { useBooking } from "../context/BookingContext";

interface DateStripProps {
  getNext5Days: (firstDay: Date) => Date[];
  slotsByDay?: Record<string, number> | null;
  showMonthAndYear?: boolean;
  className?: string;
  experityBooking?: boolean;
  primaryColor?: string;
  timezone: string;
}

/**
 * Finds the first available date in a range of dates.
 * If experityBooking is true or slotsByDay is not provided, returns the first date.
 * Otherwise, returns the first date that has slots available, or the first date if none found.
 */
function getFirstAvailableDate(
  dates: Date[],
  experityBooking: boolean,
  slotsByDay?: Record<string, number> | null,
): Date {
  if (experityBooking || !slotsByDay) {
    return dates[0];
  }
  return (
    dates.find((date) => slotsByDay?.[startOfDay(date).toString()]) ?? dates[0]
  );
}

export function DateStrip({
  getNext5Days,
  slotsByDay,
  showMonthAndYear = true,
  className,
  experityBooking = false,
  primaryColor,
  timezone,
}: DateStripProps) {
  const {
    currentDisplayDates,
    setCurrentDisplayDates,
    startOfDayAfterTomorrow,
    selectedDay,
    setSelectedDay,
    selectedAppointmentTime,
  } = useBooking();

  useEffect(() => {
    const nextDayWithSlots = currentDisplayDates.find(
      (date) => slotsByDay?.[date.toString()],
    );
    if (
      nextDayWithSlots &&
      !currentDisplayDates.find((date) => date === selectedDay)
    ) {
      setSelectedDay(nextDayWithSlots);
    }
  }, [currentDisplayDates, slotsByDay, setSelectedDay, selectedDay]);
  const isMobile = useMediaQuery("(max-width: 1024px)");

  const canGoBack =
    differenceInDays(currentDisplayDates[0], startOfDayAfterTomorrow) > 0;

  return (
    <div
      className={cn(
        "flex flex-row w-full lg:w-[77%] gap-2 items-center relative sm:px-6 lg:px-0",
        className,
      )}
    >
      <Button
        variant="ghost"
        size="icon"
        className="h-[42px] w-[42px] absolute left-[-20px]"
        disabled={!canGoBack}
        onClick={() => {
          const newDates = getNext5Days(
            new Date(
              currentDisplayDates[0].setDate(
                currentDisplayDates[0].getDate() - 5,
              ),
            ),
          );
          setCurrentDisplayDates(newDates);
          // Auto-select the first available date in the new range
          setSelectedDay(
            getFirstAvailableDate(newDates, experityBooking, slotsByDay),
          );
        }}
      >
        <ChevronLeft size={24} />
      </Button>

      <div className="flex flex-col w-full justify-center gap-0 relative">
        {showMonthAndYear && (
          <div className="w-full lg:flex absolute top-[-32px] justify-center  sm:hidden ">
            <p className="font-medium text-sm w-fit">
              {formatInTimeZone(currentDisplayDates[0], timezone, "MMM yyyy")}
            </p>
          </div>
        )}
        <div className="grid grid-cols-5 sm:gap-1 lg:gap-0">
          {currentDisplayDates.map((date, i) => {
            const doSlotsExistInDayView =
              !!slotsByDay?.[startOfDay(date).toString()] || experityBooking; // render all if experityBooking true

            const isSelected = selectedDay === date;
            const isDateOfSelectedTime = experityBooking
              ? selectedAppointmentTime instanceof Date &&
                isSameDay(date, selectedAppointmentTime)
              : isSelected;
            return (
              <Button
                variant="default"
                key={date.toISOString()}
                className={cn(
                  "h-[42px] flex flex-col items-center bg-transparent justify-center lg:px-8 sm:px-4 py-8 lg:hover:bg-transparent lg:border-none sm:border sm:border-[#e1e1e1] lg:cursor-default",
                  isSelected && "lg:bg-transparent",
                  isSelected && "sm:text-white lg:text-black hover:text-black",
                  !doSlotsExistInDayView && "sm:bg-slate-100 lg:bg-transparent",
                )}
                disabled={!doSlotsExistInDayView}
                onClick={() => {
                  setSelectedDay(date);
                }}
                style={{
                  backgroundColor:
                    isDateOfSelectedTime && experityBooking
                      ? primaryColor
                      : "transparent",

                  border:
                    isSelected && isMobile
                      ? `1px solid ${primaryColor ?? "#000000"}`
                      : "1px solid transparent",
                }}
              >
                <span
                  className={cn(
                    "font-medium text-sm text-black",
                    isSelected && "sm:text-white lg:text-black",
                  )}
                  style={{
                    color:
                      isDateOfSelectedTime && experityBooking
                        ? "white"
                        : "black",
                  }}
                >
                  {formatInTimeZone(date, timezone, "d")}
                </span>
                <span
                  className={cn(
                    "text-sm font-normal text-gray-600",
                    isSelected && "sm:text-white lg:text-black",
                  )}
                  style={{
                    color:
                      isDateOfSelectedTime && experityBooking
                        ? "white"
                        : "gray",
                  }}
                >
                  {formatInTimeZone(date, timezone, "EEE")}
                </span>
              </Button>
            );
          })}
        </div>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="h-[42px] w-[42px] absolute right-[-20px]"
        onClick={() => {
          const newDates = getNext5Days(
            new Date(
              currentDisplayDates[0].setDate(
                currentDisplayDates[0].getDate() + 5,
              ),
            ),
          );
          setCurrentDisplayDates(newDates);
          // Auto-select the first available date in the new range
          setSelectedDay(
            getFirstAvailableDate(newDates, experityBooking, slotsByDay),
          );
        }}
      >
        <ChevronRight size={24} />
      </Button>
    </div>
  );
}
